import axios from "axios";
import { getAuthHeaders } from "../utils/apiConfig";
import { isCreatedToday } from "../utils/dateUtils";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface DeliveryNotificationItem {
  id: number;
  title: string;
  message: string;
  notification_type: string;
  reference_id?: string;
  reference_type?: string;
  is_read: boolean;
  created_at: string;
  data?: Record<string, unknown>;
}

function unwrap<T>(body: unknown, fallback: T): T {
  if (!body || typeof body !== "object") return fallback;
  const first = body as Record<string, unknown>;
  const data = first.data;
  if (Array.isArray(data)) return data as unknown as T;
  if (data && typeof data === "object") {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as unknown as T;
    return data as T;
  }
  return body as T;
}

/** DRF pagination `{ count, results }` or ResponseFormatter `{ data: [...] }`. */
function parseNotificationList(body: unknown): DeliveryNotificationItem[] {
  if (!body) return [];
  if (Array.isArray(body)) return body as DeliveryNotificationItem[];

  if (typeof body !== "object") return [];
  const root = body as Record<string, unknown>;

  if (Array.isArray(root.results)) {
    return root.results as DeliveryNotificationItem[];
  }

  const data = root.data;
  if (Array.isArray(data)) {
    return data as DeliveryNotificationItem[];
  }
  if (data && typeof data === "object") {
    const inner = data as Record<string, unknown>;
    if (Array.isArray(inner.results)) {
      return inner.results as DeliveryNotificationItem[];
    }
    if (Array.isArray(inner.data)) {
      return inner.data as DeliveryNotificationItem[];
    }
  }

  return [];
}

export async function fetchNotifications(unreadOnly = false): Promise<DeliveryNotificationItem[]> {
  const res = await axios.get(`${BASE_URL}/notifications/`, {
    headers: getAuthHeaders(),
    params: unreadOnly ? { unread_only: "true" } : undefined,
  });
  return parseNotificationList(res.data);
}

/** Keep only notifications created on the current local day. */
export function filterTodayNotifications(
  items: DeliveryNotificationItem[]
): DeliveryNotificationItem[] {
  return items.filter((n) => isCreatedToday(n.created_at));
}

export function countUnreadNotifications(
  items: DeliveryNotificationItem[]
): number {
  return items.filter((n) => !n.is_read).length;
}

export async function fetchUnreadCount(): Promise<number> {
  const res = await axios.get(`${BASE_URL}/notifications/unread-count/`, {
    headers: getAuthHeaders(),
  });
  const data = unwrap<Record<string, unknown>>(res.data, {});
  const count = (data.unread_count ?? data.count) as number | string | undefined;
  return Number(count || 0);
}

export async function markNotificationRead(id: number): Promise<boolean> {
  try {
    await axios.post(`${BASE_URL}/notifications/${id}/mark-read/`, {}, { headers: getAuthHeaders() });
    return true;
  } catch {
    return false;
  }
}

export async function markAllNotificationsRead(): Promise<boolean> {
  try {
    await axios.post(`${BASE_URL}/notifications/mark-all-read/`, {}, { headers: getAuthHeaders() });
    return true;
  } catch {
    return false;
  }
}
